<template>
  <div class="chat-input">
    <b-form @submit.prevent="handleSend" class="chat-input-form">
      <div class="input-wrapper">
        <!-- Textarea for message input -->
        <b-form-textarea
          ref="messageInput"
          v-model="message"
          :placeholder="placeholder"
          :disabled="disabled"
          rows="1"
          max-rows="8"
          class="message-textarea"
          @keydown.enter.exact.prevent="handleSend"
          @keydown.shift.enter.exact="handleNewLine"
          @input="handleInput"
        />

        <!-- Action Buttons -->
        <div class="input-actions">
          <!-- Stop Streaming Button -->
          <b-button
            v-if="isStreaming"
            variant="flat-danger"
            size="sm"
            @click="$emit('stop')"
            v-b-tooltip.hover.top
            title="Arrêter la génération"
            class="action-btn"
          >
            <feather-icon icon="StopCircleIcon" size="20" />
          </b-button>

          <!-- Attach File Button -->
          <b-button
            v-if="!isStreaming && allowAttachments"
            variant="flat-secondary"
            size="sm"
            @click="$emit('attach')"
            :disabled="disabled"
            v-b-tooltip.hover.top
            title="Joindre un fichier"
            class="action-btn"
          >
            <feather-icon icon="PaperclipIcon" size="20" />
          </b-button>

          <!-- Send Button -->
          <b-button
            v-if="!isStreaming"
            type="submit"
            variant="primary"
            size="sm"
            :disabled="!canSend"
            v-b-tooltip.hover.top
            :title="sendButtonTooltip"
            class="action-btn send-btn"
          >
            <feather-icon icon="SendIcon" size="20" />
          </b-button>
        </div>
      </div>

      <!-- Character Counter -->
      <div v-if="showCharCounter && maxLength" class="char-counter">
        <small :class="{ 'text-danger': message.length > maxLength }">
          {{ message.length }} / {{ maxLength }}
        </small>
      </div>

      <!-- Input Hints -->
      <div v-if="showHints" class="input-hints">
        <small class="text-muted">
          <feather-icon icon="InfoIcon" size="14" class="mr-25" />
          Appuyez sur <kbd>Entrée</kbd> pour envoyer, <kbd>Shift + Entrée</kbd> pour une nouvelle ligne
        </small>
      </div>
    </b-form>
  </div>
</template>

<script>
import {
  BForm,
  BFormTextarea,
  BButton,
  VBTooltip
} from 'bootstrap-vue'

export default {
  name: 'ChatInput',
  components: {
    BForm,
    BFormTextarea,
    BButton
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Tapez votre message...'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isStreaming: {
      type: Boolean,
      default: false
    },
    allowAttachments: {
      type: Boolean,
      default: true
    },
    maxLength: {
      type: Number,
      default: 4000
    },
    showCharCounter: {
      type: Boolean,
      default: false
    },
    showHints: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      message: this.value
    }
  },
  computed: {
    canSend() {
      return this.message.trim().length > 0 &&
             this.message.length <= this.maxLength &&
             !this.disabled
    },
    sendButtonTooltip() {
      if (this.disabled) return 'Entrée désactivée'
      if (!this.message.trim()) return 'Saisissez un message'
      if (this.message.length > this.maxLength) return 'Message trop long'
      return 'Envoyer le message (Entrée)'
    }
  },
  watch: {
    value(newVal) {
      this.message = newVal
    },
    message(newVal) {
      this.$emit('input', newVal)
    }
  },
  methods: {
    handleSend() {
      if (!this.canSend) return

      const messageToSend = this.message.trim()
      this.$emit('send', messageToSend)
      this.message = ''

      // Reset textarea height
      this.$nextTick(() => {
        this.resetTextareaHeight()
      })
    },
    handleNewLine() {
      // Allow default behavior (new line)
      // This is handled by the textarea itself
    },
    handleInput() {
      this.$emit('typing')
    },
    resetTextareaHeight() {
      if (this.$refs.messageInput) {
        this.$refs.messageInput.$el.style.height = 'auto'
      }
    },
    focus() {
      this.$nextTick(() => {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.$el.focus()
        }
      })
    },
    clear() {
      this.message = ''
      this.resetTextareaHeight()
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-input {
  background: white;
  border-top: 1px solid #ebe9f1;
  padding: 1rem 1.5rem;
}

.chat-input-form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  position: relative;
}

.message-textarea {
  flex: 1;
  resize: none;
  border-radius: 20px;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  line-height: 1.5;
  border: 2px solid #ebe9f1;
  transition: all 0.3s ease;

  &:focus {
    border-color: var(--primary);
    box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.15);
  }

  &:disabled {
    background-color: #f8f8f8;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #b9b9c3;
  }
}

.input-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-btn {
  border-radius: 50%;
  width: 40px;
  height: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: scale(1.1);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &.send-btn {
    &:not(:disabled) {
      background: linear-gradient(135deg, var(--primary) 0%, #7367f0 100%);
      box-shadow: 0 4px 12px rgba(115, 103, 240, 0.4);

      &:hover {
        box-shadow: 0 6px 16px rgba(115, 103, 240, 0.5);
      }
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.char-counter {
  text-align: right;
  margin-top: -0.25rem;
}

.input-hints {
  padding: 0.25rem 0.5rem;
  background: #f8f8f8;
  border-radius: 8px;
  text-align: center;

  kbd {
    background: white;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
    border: 1px solid #d8d6de;
    font-size: 0.85em;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
}

// Responsive
@media (max-width: 576px) {
  .chat-input {
    padding: 0.75rem 1rem;
  }

  .action-btn {
    width: 36px;
    height: 36px;

    ::v-deep svg {
      width: 18px;
      height: 18px;
    }
  }

  .input-hints {
    display: none;
  }
}
</style>
